import {
  MouseEvent,
  useMemo,
  useCallback,
  ElementType,
  ReactElement,
  createElement,
} from 'react';
import { useHistory, createLocation } from 'quartz-routing/react-router';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import { LinkTarget, MergeElementProps } from './types';

interface OwnProps {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  to: LinkTarget;
  replace?: boolean;
}

export type RouterLinkProps<C extends ElementType> = MergeElementProps<
  C,
  OwnProps
>;

type AbstractRouterLinkProps<C extends ElementType> = RouterLinkProps<C> & {
  component: C;
};

const AbstractRouterLink = <C extends ElementType>(
  props: AbstractRouterLinkProps<C>
): ReactElement => {
  const { component, to = '', replace = false, onClick, ...rest } = props;

  const history = useHistory();

  const handleClick = useCallback(
    (e: MouseEvent<HTMLElement>): void => {
      callIfPossible(onClick, e);

      if (e.defaultPrevented) {
        return;
      }

      if (e && !e.metaKey) {
        e.preventDefault();

        if (replace) {
          history.replace(to);
        } else {
          history.push(to);
        }
      }
    },
    [history, onClick, replace, to]
  );

  const href: string = useMemo(() => {
    const location =
      typeof to === 'string' || to === null
        ? createLocation(to || '', null, undefined, history.location)
        : to;

    return history.createHref(location);
  }, [history, to]);

  return createElement(component, { ...rest, href, onClick: handleClick });
};

export default AbstractRouterLink;
